import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useMediaQuery, SwipeableDrawer, Snackbar, Alert } from "@mui/material";
import { useAppState } from "../state/AppState";
import { useLang } from "../i18n/LangContext";
import type { Spot } from "../types";
import { MapView } from "../components/MapView";
import { Sidebar } from "../components/Sidebar";
import { SpotDetail } from "../components/SpotDetail";
import { FilterFab } from "../components/FilterFab";
import { FilterResetPill } from "../components/FilterResetPill";
import { FiltersPanel } from "../components/FiltersPanel";
import { ViewportDebug } from "../components/ViewportDebug";
import { SEOHead, SITE_URL } from "../components/SEOHead";
import { SpotSearchSheet } from "../components/SpotSearchSheet";
import Fab from "@mui/material/Fab";
import Tooltip from "@mui/material/Tooltip";
import SearchIcon from "@mui/icons-material/Search";

export function MapPage() {
  const { spots, selectedId, selectSpot, loadError } = useAppState();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const isMobile = useMediaQuery("(max-width: 900px)");
  const [errorOpen, setErrorOpen] = useState(false);
  const [searchSheetOpen, setSearchSheetOpen] = useState(false);

  const filterSheetOpen = location.pathname.endsWith("/filter");

  useEffect(() => {
    if (loadError) setErrorOpen(true);
  }, [loadError]);

  // Body data-route lets CSS apply the full-bleed PWA layout (fixed body,
  // hidden overflow) for the map only. Content pages keep the default
  // scrollable body.
  useEffect(() => {
    document.body.setAttribute("data-route", "map");
    return () => document.body.removeAttribute("data-route");
  }, []);

  useEffect(() => {
    document.body.toggleAttribute("data-filter-open", filterSheetOpen);
  }, [filterSheetOpen]);
  useEffect(() => {
    document.body.toggleAttribute("data-detail-open", !!selectedId);
  }, [selectedId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && (selectedId || filterSheetOpen)) {
        navigate(-1);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectedId, filterSheetOpen, navigate]);

  const selectedSpot = selectedId
    ? spots.find((s) => s.id === selectedId) ?? null
    : null;

  const [renderedSpot, setRenderedSpot] = useState<Spot | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  useEffect(() => {
    if (selectedSpot) {
      setRenderedSpot(selectedSpot);
      if (navigationType === "POP") {
        setDetailVisible(true);
        return;
      }
      const id = requestAnimationFrame(() => setDetailVisible(true));
      return () => cancelAnimationFrame(id);
    }
    setDetailVisible(false);
    const tid = setTimeout(() => setRenderedSpot(null), 260);
    return () => clearTimeout(tid);
  }, [selectedSpot, navigationType]);

  const closeDetail = useCallback(() => selectSpot(null), [selectSpot]);
  const openSheet = useCallback(() => navigate("/filter"), [navigate]);
  const closeSheet = useCallback(() => navigate(-1), [navigate]);

  // SEO: when a spot is in focus, set a per-spot title + Place schema.
  const spotForSeo = selectedSpot;
  const seoTitle = spotForSeo
    ? `${spotForSeo.name}${spotForSeo.river ? ` — ${spotForSeo.river}` : ""}`
    : t("navMap");
  const seoDescription = spotForSeo
    ? (spotForSeo.descriptionDe ||
        spotForSeo.descriptionEn ||
        spotForSeo.description ||
        `${spotForSeo.name} — ${t("spotsLabel")}.`).slice(0, 180)
    : "Live-Karte aller Veiðisvæði in Island — Lachs, Forelle, Saibling.";
  const seoPath = spotForSeo ? `/spot/${spotForSeo.id}` : "/map";
  const seoJsonLd = spotForSeo
    ? {
        "@context": "https://schema.org",
        "@type": "Place",
        name: spotForSeo.name,
        ...(spotForSeo.description && { description: spotForSeo.description }),
        geo: {
          "@type": "GeoCoordinates",
          latitude: spotForSeo.lat,
          longitude: spotForSeo.lon,
        },
        url: `${SITE_URL}/spot/${spotForSeo.id}`,
        inLanguage: lang,
      }
    : undefined;

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        path={seoPath}
        jsonLd={seoJsonLd}
      />
      <div id="app">
        <Sidebar mobile={isMobile} />
        <MapView />
        {renderedSpot && (
          <SpotDetail
            spot={renderedSpot}
            onClose={closeDetail}
            hidden={!detailVisible}
          />
        )}
      </div>
      {isMobile && (
        <>
          <SwipeableDrawer
            anchor="bottom"
            open={filterSheetOpen}
            onOpen={openSheet}
            onClose={closeSheet}
            disableSwipeToOpen
            swipeAreaWidth={0}
            PaperProps={{
              sx: {
                bgcolor: "background.paper",
                backgroundImage: "none",
                borderRadius: "18px 18px 0 0",
                maxHeight: "85dvh",
                display: "flex",
                flexDirection: "column",
              },
            }}
            ModalProps={{ keepMounted: true }}
          >
            <FiltersPanel onClose={closeSheet} open inSheet />
          </SwipeableDrawer>
          <SpotSearchSheet
            open={searchSheetOpen}
            onOpen={() => setSearchSheetOpen(true)}
            onClose={() => setSearchSheetOpen(false)}
          />
          <FilterFab onClick={openSheet} active={filterSheetOpen} />
          {!filterSheetOpen && !selectedId && !searchSheetOpen && (
            <Tooltip title={t("searchPlaceholder")} placement="left">
              <Fab
                size="small"
                color="primary"
                aria-label={t("searchPlaceholder")}
                onClick={() => setSearchSheetOpen(true)}
                className="search-fab"
              >
                <SearchIcon sx={{ color: "common.white" }} />
              </Fab>
            </Tooltip>
          )}
          {!filterSheetOpen && !searchSheetOpen && !selectedId && <FilterResetPill />}
        </>
      )}
      <ViewportDebug />
      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErrorOpen(false)}>
          {loadError ?? ""}
        </Alert>
      </Snackbar>
    </>
  );
}
